import React, { useEffect, useState } from "react";
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import { ProductImageThumbs, imagesOf } from "../../../common/ImageLightbox";
import { Grid, MenuItem, Select, Typography, Box, Card, CardContent, Stack, Chip } from "@mui/material";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { apiConstants, post, postGeneric } from "../../../common/constants";
import { FormProvider, RHFRadioGroup, RHFSelectField, RHFTextField } from "../../../common/hook-form";
import { useForm } from "react-hook-form";
import { useGetCouriersQuery } from "../../../redux/commonRTKPersist";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import { useSnackbar } from "notistack";
import ConfirmDialog from "../../../common/ConfirmDialog";
import { formatCurrency, parseDateAndTime } from "../../../helpers/UtilityHelper";
import { addDays, format } from "date-fns";
import { CourierSkeleton } from "../../../common/Skeletons";


// The courier rule-engine can return a message whose "Possible reason:[...]" list
// repeats the same failure hundreds of times (one entry per courier), which makes the
// error snackbar balloon and overlap the page. Collapse it: keep the headline and show
// each distinct failed rule ONCE. Harmless for normal (short) messages.
const summarizeRuleError = (message, fallback = 'Failed to process return request') => {
    if (!message || typeof message !== 'string') return fallback;
    const bracket = message.match(/\[(.*)\]/s);
    if (!bracket) return message.length > 300 ? `${message.slice(0, 300)}…` : message;
    const head = message.slice(0, bracket.index).replace(/Possible reason:\s*$/i, '').trim();
    const reasons = [];
    for (const raw of bracket[1].split(',')) {
        const r = raw.replace(/Rule failed:?/i, '').trim();
        if (r && !reasons.includes(r)) reasons.push(r);
    }
    const summary = reasons.length ? `${head} Failed checks: ${reasons.join(', ')}.` : head;
    return summary.length > 300 ? `${summary.slice(0, 300)}…` : summary;
};

// ---- Presentational helpers for the modern "Manage Return Request" layout ----
const Section = ({ icon, title, children, noPad }) => (
    <Card elevation={0} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 1.25,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: 'grey.50',
        }}>
            {icon}
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{title}</Typography>
        </Box>
        <CardContent sx={{ p: noPad ? 0 : 2, '&:last-child': { pb: noPad ? 0 : 2 } }}>
            {children}
        </CardContent>
    </Card>
);

const SummaryItem = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.8 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{value}</Typography>
    </Box>
);

// Return name / reason / description are customer-typed and can run long: wrap the whole text and let
// the row grow to fit it (the grid below uses auto row height) instead of cutting it off with "...".
const wrapTextSx = { py: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 };

const ApproveRequest = ({ approveModalData, itemModalData, setApproveModalOpen, setReload }) => {
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [isApproveLoading, setApproveLoading] = useState(false);
    const [isRejectLoading, setRejectLoading] = useState(false);
    const [rejectConfirm, setRejectConfirm] = useState(false);
    const [confirmApprove, setConfirmApprove] = useState(false);
    const [selectedRowsData, setSelectedRowsData] = useState([]);
    const [newCourier, setNewCourier] = useState([]);
    const [courierPricingLoading, setCourierPricingLoading] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const { data: allCouriers } = useGetCouriersQuery()
    const { enqueueSnackbar } = useSnackbar();
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''
    // sku_id -> Product Master images[] for the items in this return request
    const [skuImageMap, setSkuImageMap] = useState({});

    // Fetch Product Master catalog images for the return's SKUs. Uses product/get/all, which a
    // merchant login can call (tools/generic/search rejects non-Proship-admins for product_master).
    // It is scoped to the caller's merchant for merchant logins but is SKU-only for Proship admins,
    // and SKU codes are only unique per merchant, so keep just the rows owned by the return's
    // merchant OID (= product_master.merchantId). Keyed by sku_id for the column.
    const imageOwnerOid = itemModalData?.merchantOid || merchantId;
    useEffect(() => {
        const skus = [...new Set((approveModalData || []).map((r) => r?.item?.sku_id).filter(Boolean))];
        if (!skus.length) {
            setSkuImageMap({});
            return;
        }
        postGeneric(apiConstants.GET_SKU_DIM, { skuList: skus })
            .then((res) => {
                const map = {};
                for (const p of Array.isArray(res?.data) ? res.data : []) {
                    if (!p?.skuCode) continue;
                    if (imageOwnerOid && String(p.merchantId) !== String(imageOwnerOid)) continue;
                    map[p.skuCode] = imagesOf(p);
                }
                setSkuImageMap(map);
            })
            .catch((err) => {
                console.log('product-master image fetch failed', err);
                setSkuImageMap({});
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [approveModalData, imageOwnerOid]);

    // The customer's return-request images are time-limited S3 signed URLs that
    // frequently arrive already expired. Re-sign on click via REFRESH_URL_S3 and
    // open in a new tab. A blank tab is opened synchronously first so the popup
    // survives the await (popup blockers otherwise kill an async window.open).
    const handleViewReturnImage = async (raw) => {
        const urls = Array.isArray(raw)
            ? raw.filter(Boolean)
            : raw && raw !== 'NA' && raw !== 'null'
                ? String(raw).split(',').map((u) => u.trim()).filter(Boolean)
                : [];
        if (!urls.length) return;
        const win = window.open('', '_blank');
        try {
            const res = await postGeneric(apiConstants.REFRESH_URL_S3, { signedUrl: urls[0] });
            const fresh = typeof res?.data === 'string' ? res.data : urls[0];
            if (win) win.location.href = fresh; else window.open(fresh, '_blank');
        } catch (err) {
            if (win) win.location.href = urls[0]; else window.open(urls[0], '_blank');
            enqueueSnackbar('Could not refresh the image link', { variant: 'warning' });
        }
    };

    const generateQuantityOptions = (currentQuantity) => {
        const options = [];
        for (let i = currentQuantity; i >= 1; i--) {
            options.push({ label: `${i}`, value: i });
        }
        return options;
    };


    const methods = useForm({ defaultValues: { courier_type: "Prozo Choice" } })
    const { handleSubmit, watch, setValue } = methods;

    const courierSelection = watch('courier_type')

    const handleQuantityChange = (rowId, newValue) => {
        setSelectedQuantities(prevState => ({
            ...prevState,
            [rowId]: newValue
        }));
    };

    const handleSelectionModelChange = (newSelection) => {
        const selectedRows = rowsWithId.filter(row => newSelection.includes(row.id));
        setSelectedRowsData(selectedRows);
    };

    const updatedRowsData = selectedRowsData.map(row => ({
        ...row,
        selectedQuantity: selectedQuantities[row.id] !== undefined ? selectedQuantities[row.id] : row?.remainingActionableItems
    }));
    // reject items
    const handleReject = () => {
        setRejectLoading(true)
        const itemDetail = updatedRowsData?.map(item => {
            return {
                [item.item?.sku_id]: item.selectedQuantity
            };
        });
        const payload = {
            "id": itemModalData.pk_id,
            "rejectedItems": itemDetail,
            "fromMerchant": merchantId
        }
        postGeneric((`${apiConstants.REJECT_RETURN_REQUEST}`), payload).then((res) => {
            setRejectLoading(false)
            setApproveModalOpen(false)
            setReload(reload => reload + 1)
            enqueueSnackbar('Return Request for Order has been Rejected', { variant: 'success' })
        }).catch((err) => {
            setRejectLoading(false)
            setApproveModalOpen(false)
            enqueueSnackbar(err?.response?.data?.message || 'Some error occurred', { variant: 'error' })
            console.log(err);
        })

    }
    // approve items
    const onSubmit = () => {
        if (selectedRowsData.length > 0) {
            setConfirmApprove(true);
        } else {
            enqueueSnackbar('Please select at least one item to approve.', { variant: 'error' })
        }

    };
    const handleDialogConfirm = (data) => {
        handleApproveAction(data);
    };

    const handleApproveAction = async (data) => {
        try {
            setApproveLoading(true);

            const shipment_detail = data.shipment_detail?.map(item => ({
                ...item,
                customDWSWeight: 50
            }));

            const itemDetail = updatedRowsData?.map(item => ({
                [item.item?.sku_id]: item.selectedQuantity
            }));

            const itemRvpMap = updatedRowsData?.map(item => ({
                [item.item?.sku_id]: "RVP reason"
            }));

            const skipRule = courierSelection === "Other";

            // First API call
            const approvePayload = {
                skipRule,
                courier: data.courierPartner,
                itemSkuMap: itemDetail,
                itemRvpMap,
                invoiceValue: data.invoiceValue,
                shipment_detail,
                ...(skipRule ? {} : { courier: data.courierPartner })
            };

            // Send the return's merchant as a query param (not a header) so the backend
            // owns the reverse order under the correct brand — and no CORS preflight fires.
            const returnMerchantOid = itemModalData?.merchantOid || merchantId;
            const approveResponse = await postGeneric(
                `${apiConstants.APPROVE_RETURN_REQUEST}?id=${itemModalData.pk_id}&merchantOid=${returnMerchantOid}`,
                approvePayload
            );
            // Second API call if first one succeeds
            if (approveResponse?.status === 200) {
                const acceptPayload = {
                    id: itemModalData.pk_id,
                    acceptedItems: itemDetail,
                    fromMerchant: merchantId
                };

                await postGeneric(apiConstants.ACCEPT_RETURN_REQUEST, acceptPayload);
            }
            enqueueSnackbar(approveResponse?.data?.meta?.message, { variant: 'success' });
            handleCloseModal();

        } catch (error) {
            enqueueSnackbar(summarizeRuleError(error?.response?.data?.message, 'Failed to process return request'), { variant: 'error' });
            console.error('Return request error:', error);
        } finally {
            setApproveLoading(false);
        }
    };

    const handleCloseModal = () => {
        setApproveModalOpen(false);
        setReload(prev => prev + 1);
    };


    const rowsWithId = approveModalData.map((row, index) => ({
        ...row,
        id: index,
    }));

    const handleRejectAction = () => {
        handleReject()
    };

    const handleRejectConfirmation = (e) => {
        if (selectedRowsData.length > 0) {
            setRejectConfirm(true);
        } else {
            enqueueSnackbar('Please select at least one item to reject.', { variant: 'error' })
        }
    };

    useEffect(() => {
        if (itemModalData?.isAllOrderReturn) {
            const payload = {
                awbNumber: itemModalData?.awbNumber,
                isAllReturn: itemModalData?.isAllOrderReturn
            }
            postGeneric((`${apiConstants.GET_RETURN_SHIPMENT_DETAILS}?merchantOid=${merchantId}`), payload).then((res) => {
                setValue("invoiceValue", res?.data?.invoiceValue)
                setValue("shipment_detail.0.item_breadth", res?.data?.item_breadth)
                setValue("shipment_detail.0.item_height", res?.data?.item_height)
                setValue("shipment_detail.0.item_length", res?.data?.item_length)
                setValue("shipment_detail.0.item_weight", res?.data?.item_weight)
            }).catch((err) => {
                console.log(err);
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (courierSelection !== "Other") {
            setDataFetched(false);
        }
    }, [courierSelection])

    useEffect(() => {
        setDataFetched(false);
    }, [itemModalData?.pk_id])

    useEffect(() => {
        if (courierSelection === "Other" && !dataFetched && allCouriers?.length &&
            itemModalData?.pickupDetails?.FromPincode && itemModalData?.deliveryDetails?.ToPincode) {
            const formValues = methods.getValues();
            const shipmentDetail = formValues?.shipment_detail;
            const length = _.sumBy(shipmentDetail, "item_length") || 0;
            const breadth = _.sumBy(shipmentDetail, "item_breadth") || 0;
            const height = _.sumBy(shipmentDetail, "item_height") || 0;
            const weight = _.sumBy(shipmentDetail, "item_weight") || 0;
            const invoiceValue = formValues?.invoiceValue || 0;
            const fromPincode = parseInt(itemModalData?.deliveryDetails?.ToPincode, 10);
            const toPincode = parseInt(itemModalData?.pickupDetails?.FromPincode, 10);
            if (isNaN(fromPincode) || isNaN(toPincode)) return;
            setCourierPricingLoading(true);
            post(`${apiConstants.BASE_LOCAL}${apiConstants.SHIPMENT_RATE_CALCULATOR}`, {
                orderType: "RVP",
                to: toPincode,
                from: fromPincode,
                paymentType: "PREPAID",
                length,
                breadth,
                height,
                weight,
                dispatchMode: "SURFACE",
                codAmount: 0,
                isMerchant: true,
                onlyCouriersAsPerRuleMapping: true, checkServiceable: true,
                merchantOid: merchantId,
                invoiceValue
            }).then((res) => {
                setCourierPricingLoading(false);
                const pricing = res?.data;
                const mergedCourier = allCouriers?.map((d) => {
                    const updatedCourier = { ...d };
                    pricing?.forEach((t) => {
                        if (t.provider?.id === updatedCourier.prozoId) {
                            updatedCourier.priceAfterTax = t.priceAfterTax;
                            updatedCourier.providerMinTatDays = t.providerMinTatDays;
                            updatedCourier.providerMaxTatDays = t.providerMaxTatDays;
                        }
                    });
                    return updatedCourier;
                }) || [];
                const filteredCouriers = mergedCourier.filter(item =>
                    item.priceAfterTax && item.type === "RVP"
                );
                const now = new Date();
                filteredCouriers.forEach(shipment => {
                    const minTatDays = shipment.providerMinTatDays || 0;
                    const maxTatDays = shipment.providerMaxTatDays || 0;
                    shipment.minDeliveryDate = format(addDays(now, minTatDays), "dd, MMM");
                    shipment.maxDeliveryDate = format(addDays(now, maxTatDays), "dd, MMM");
                });
                setNewCourier(filteredCouriers);
                setDataFetched(true);
            }).catch((err) => {
                console.log(err);
                enqueueSnackbar(summarizeRuleError(err?.response?.data?.message, "Error fetching pricing"), { variant: "error" });
                setCourierPricingLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courierSelection, dataFetched, itemModalData?.pickupDetails?.FromPincode, itemModalData?.deliveryDetails?.ToPincode, itemModalData?.pk_id, allCouriers, merchantId])

    const columns = [
        {
            field: "item",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Name",
            flex: 0.8,
            minWidth: 150,
            renderCell: ({ row }) => <Box sx={wrapTextSx}>{row.item?.item_name}</Box>,
        },
        {
            field: "sku_id",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "SKU ID",
            flex: 0.5,
            renderCell: ({ row }) => {
                return row.item?.sku_id;
            },
        },
        {
            field: "selling_price",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Selling Price",
            flex: 0.5,
            renderCell: ({ row }) => {
                return row.item?.selling_price;
            },
        },
        {
            field: "skuRvpReason",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Return Reason",
            flex: 0.9,
            minWidth: 170,
            renderCell: ({ row }) => <Box sx={wrapTextSx}>{row.item?.skuRvpReason}</Box>,
        },
        {
            field: "skuRvpDescription",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Description",
            flex: 1.2,
            minWidth: 220,
            renderCell: ({ row }) => <Box sx={wrapTextSx}>{row?.rvpDesc}</Box>,
        },
        {
            field: "units",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Units",
            flex: 0.5,
            renderCell: ({ row }) => {
                const options = generateQuantityOptions(row?.remainingActionableItems);
                const selectedQuantity = selectedQuantities[row.id] !== undefined ? selectedQuantities[row.id] : row?.remainingActionableItems;
                return (
                    <Select
                        value={selectedQuantity}
                        onChange={(event) => { handleQuantityChange(row.id, event.target.value) }}
                        sx={{ width: 100, height: 40 }}
                    >
                        {options.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                );
            }
        },
        {
            // Customer's return-request photo(s). Signed S3 URLs expire, so the View
            // button re-signs via REFRESH_URL_S3 (handleViewReturnImage) before opening.
            field: "returnImagesUrl",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Return Image",
            flex: 0.5,
            renderCell: ({ row }) => {
                const raw = row?.returnImagesUrl;
                const has = Array.isArray(raw) ? raw.filter(Boolean).length > 0 : (raw && raw !== 'NA' && raw !== 'null');
                return has ? (
                    <Typography
                        variant='subtitle2'
                        sx={{ color: '#1565c0', textDecoration: 'underline', display: 'inline', cursor: 'pointer', fontWeight: 600, '&:hover': { color: '#0d47a1' } }}
                        onClick={() => handleViewReturnImage(raw)}
                    >
                        View
                    </Typography>
                ) : (
                    <Typography sx={{ color: '#8A94A6', fontSize: 12 }}>No image</Typography>
                );
            },
        },
        {
            // Catalog images from Product Master for this SKU (fetched by merchantId
            // + skuCode). Public CloudFront URLs (don't expire) — shown as thumbnails;
            // click opens the in-app carousel.
            field: "productImages",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Catalog Image",
            flex: 0.6,
            renderCell: ({ row }) => (
                <ProductImageThumbs images={skuImageMap[row?.item?.sku_id]} size={34} empty="No image" />
            ),
        },
    ];

    return (
        <Box sx={{ fontFamily: 'Poppins, sans-serif' }}>
            {/* ---- Header summary ---- */}
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
                p: 2, mb: 2, borderRadius: 2, color: '#fff',
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)',
            }}>
                <Box sx={{ flexGrow: 1, display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1.5 }}>
                    <SummaryItem label="Forward AWB" value={itemModalData?.awbNumber || '-'} />
                    <SummaryItem label="Client Order ID" value={itemModalData?.clientOrderId || '-'} />
                    <SummaryItem label="Customer Phone" value={itemModalData?.userPhoneNumber || '-'} />
                    <SummaryItem label="Return Requested" value={itemModalData?.orderRequestedDate ? parseDateAndTime(itemModalData?.orderRequestedDate) : '-'} />
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={itemModalData?.isAllOrderReturn ? 'Order-wise Return' : 'SKU-wise Return'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 500 }} />
                    <Chip label="Pending" size="small" sx={{ bgcolor: '#f6ad55', color: '#1a202c', fontWeight: 600 }} />
                </Stack>
            </Box>

            {/* ---- Items to return ---- */}
            <Section icon={<Inventory2OutlinedIcon fontSize="small" color="action" />} title="Items to Return" noPad>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={rowsWithId}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    onSelectionModelChange={handleSelectionModelChange}
                    checkboxSelection={true}
                    hideFooterPagination
                    rowCount={Number.MAX_VALUE}
                    getRowId={(row) => row.id}
                />
            </Section>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {/* ---- Pickup & Delivery ---- */}
                <Section icon={<PlaceOutlinedIcon fontSize="small" color="action" />} title="Pickup & Delivery">
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>Pickup Address</Typography>
                            <Typography sx={{ fontSize: '0.82rem' }}>{itemModalData?.pickupDetails ? `${itemModalData?.pickupDetails?.FromAddress}, ${itemModalData?.pickupDetails?.FromCity}, ${itemModalData?.pickupDetails?.FromState}, ${itemModalData?.pickupDetails?.FromPincode}` : 'No Details'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>Delivery Address</Typography>
                            <Typography sx={{ fontSize: '0.82rem' }}>{itemModalData?.deliveryDetails ? `${itemModalData?.deliveryDetails?.ToAddress}, ${itemModalData?.deliveryDetails?.ToCity}, ${itemModalData?.deliveryDetails?.ToState}, ${itemModalData?.deliveryDetails?.ToPincode}` : 'No Details'}</Typography>
                        </Grid>
                    </Grid>
                </Section>

                {/* ---- Package details ---- */}
                <Section icon={<ReceiptLongOutlinedIcon fontSize="small" color="action" />} title="Package Details">
                    <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <RHFTextField required variant="outlined" name="shipment_detail.0.item_weight" label="Weight (gms)" type="number" placeholder="Enter Weight"
                            value={watch("shipment_detail.0.item_weight") || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RHFTextField required variant="outlined" name="shipment_detail.0.item_length" label="Length (cm)" type="number" placeholder="Enter Length"
                            value={watch("shipment_detail.0.item_length") || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RHFTextField required variant="outlined" name="shipment_detail.0.item_breadth" label="Breadth (cm)" type="number" placeholder="Enter Breadth"
                            value={watch("shipment_detail.0.item_breadth") || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RHFTextField required variant="outlined" name="shipment_detail.0.item_height" label="Height (cm)" type="number" placeholder="Enter Height"
                            value={watch("shipment_detail.0.item_height") || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RHFTextField required variant="outlined" name="invoiceValue" label="Invoice Value" type="number" placeholder="Enter Invoice Value"
                            value={watch("invoiceValue") || ''}
                        />
                    </Grid>
                    </Grid>
                </Section>

                {/* ---- Courier selection ---- */}
                <Section icon={<LocalShippingOutlinedIcon fontSize="small" color="action" />} title="Courier Selection">
                    <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <RHFRadioGroup label="Courier Selection" name="courier_type" options={[
                            {
                                label: "As per Recommendation Rules",
                                value: "Prozo Choice",
                            },
                            { label: "Other Courier", value: "Other" },
                        ]}
                        />
                    </Grid>
                    {courierSelection === 'Other' && (
                        <Grid item xs={12}>
                            {courierPricingLoading ? (
                                <CourierSkeleton />
                            ) : (
                                <RHFSelectField
                                    variant="filled"
                                    width="100%"
                                    name="courierPartner"
                                    label="Courier Partner"
                                    noOptionsText='No courier partner available for this pickup location'
                                    options={(newCourier?.filter(row => !row.isExternal) || []).map((c) => ({
                                        label: (
                                            <Grid container spacing={0}>
                                                <Grid item xs={4}>
                                                    <Typography variant="body2">{c?.name}</Typography>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Typography variant="body2">{formatCurrency(c?.priceAfterTax)}</Typography>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Typography variant="body2">
                                                        {c.providerMinTatDays !== undefined && c.providerMaxTatDays !== undefined && c.providerMaxTatDays > 0
                                                            ? `TAT: ${c.providerMinTatDays}-${c.providerMaxTatDays} Days`
                                                            : "TAT: Not Available"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Typography variant="body2">
                                                        {c.providerMinTatDays !== undefined && c.providerMaxTatDays !== undefined && c.providerMaxTatDays > 0
                                                            ? `EDD: ${c.minDeliveryDate}-${c.maxDeliveryDate}`
                                                            : "EDD: Not Available"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        ),
                                        value: c?.prozoId
                                    }))}
                                />
                            )}
                        </Grid>
                    )}
                    </Grid>
                </Section>

                {/* ---- Actions ---- */}
                <Box sx={{
                    display: 'flex', justifyContent: 'flex-end', gap: 1.5,
                    py: 2, mt: 1,
                    borderTop: '1px solid', borderColor: 'divider',
                }}>
                    <LoadingButton
                        loading={isRejectLoading}
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            handleRejectConfirmation();
                        }}
                    >
                        Reject
                    </LoadingButton>
                    <LoadingButton
                        loading={isApproveLoading}
                        variant="contained"
                        type="submit"
                    >
                        Approve Return
                    </LoadingButton>
                </Box>
            </FormProvider>
            <ConfirmDialog
                title=''
                open={rejectConfirm}
                setOpen={setRejectConfirm}
                onConfirm={() => handleRejectAction()}>
                Are you sure you want to reject this item ?
            </ConfirmDialog>
            <ConfirmDialog
                title=''
                open={confirmApprove}
                setOpen={setConfirmApprove}
                onConfirm={() => handleDialogConfirm(methods.getValues())}
            >
                Are you sure you want to approve this item?
            </ConfirmDialog>
        </Box>
    );
};

export default ApproveRequest;
